import React from 'react';
import {
    WorkType,
    WorkType as WorkTypeEnum,
    ReviewStatus
} from 'src/enums/work';
import { IAttachment } from 'src/types/attachments';
import { ActivityHeader, TaskListContainer } from './styled';
import { getWorkTypeName, getReviewStatusName } from './utils';
import AttachmentActivityHeader from './AttachmentActivityHeader';
import TaskTitleLink from './TaskTitleLink';

interface Props {
    user: string;
    workType?: WorkTypeEnum;
    taskTitle?: string;
    taskId?: string;
    grossWorkTime?: string;
    attachments?: IAttachment[];
    reviewStatus?: ReviewStatus;
    seniorReviewerName?: string;
    rejectionReason?: string;
    solveByReviewer?: boolean;
    tasksToCreate?: string[];
    tasksDependencies?: { [taskTitle: string]: (string | number)[] };
}

const WorkSolvedActivity: React.FC<Props> = ({
    user,
    workType,
    taskTitle,
    taskId,
    grossWorkTime,
    attachments,
    reviewStatus,
    seniorReviewerName,
    rejectionReason,
    solveByReviewer,
    tasksToCreate,
    tasksDependencies
}) => {
    if (attachments && attachments.length > 0) {
        return (
            <AttachmentActivityHeader
                text={attachments}
                workType={workType}
                taskTitle={taskTitle}
                taskId={taskId}
                grossWorkTime={grossWorkTime}
                user={user}
                reviewStatus={reviewStatus}
                seniorReviewerName={seniorReviewerName}
            />
        );
    }

    const workTypeText = workType ? getWorkTypeName(workType) : '';

    if (workType === WorkType.QUEST_BREAKDOWN) {
        const taskList = tasksToCreate?.map((task, index) => {
            const rawDependencies = tasksDependencies?.[task];
            const dependencies: (string | number)[] = rawDependencies
                ? (rawDependencies as (string | number)[]).filter(
                      (dep) => dep !== null && dep !== undefined
                  )
                : [];
            const depsText =
                dependencies.length > 0
                    ? ` which depends on ${dependencies
                          .map((dep) =>
                              typeof dep === 'number' ? `#${dep + 1}` : dep
                          )
                          .join(', ')}`
                    : '';
            return (
                <div key={index}>
                    #{index + 1} <strong>{task}</strong>
                    {depsText}
                </div>
            );
        });
        return (
            <ActivityHeader>
                <strong>{user}</strong> solved <strong>{workTypeText}</strong>{' '}
                work <TaskTitleLink taskId={taskId} title={taskTitle} /> after{' '}
                {grossWorkTime ? grossWorkTime : ''} and created{' '}
                {tasksToCreate?.length} tasks:
                <TaskListContainer>{taskList}</TaskListContainer>
            </ActivityHeader>
        );
    }

    if (workType === WorkType.QUEST_REVIEW) {
        return (
            <ActivityHeader>
                <strong>{user}</strong> rejected the{' '}
                <strong>{workTypeText}</strong> work{' '}
                <TaskTitleLink taskId={taskId} title={taskTitle} /> after{' '}
                {grossWorkTime ? grossWorkTime : ''} and requested
                modifications.
                <br />
                Rejection reason: {rejectionReason}
                <br />
                {solveByReviewer
                    ? 'The request fixes will be solved by the reviewer'
                    : 'The request moved back to another breakdown iteration'}
            </ActivityHeader>
        );
    }

    if (workType === WorkType.REVIEW_REQUEST_REJECTION) {
        return (
            <ActivityHeader>
                <strong>{user}</strong> solved the{' '}
                <strong>{workTypeText}</strong> work{' '}
                <TaskTitleLink taskId={taskId} title={taskTitle} /> after{' '}
                {grossWorkTime ? grossWorkTime : ''}{' '}
                {solveByReviewer
                    ? 'The request fixes will be solved by the reviewer'
                    : 'The request moved back to another breakdown iteration'}
            </ActivityHeader>
        );
    }

    if (workType === WorkType.COMMUNITY_REVIEW) {
        return (
            <ActivityHeader>
                <strong>{user}</strong> solved <strong>{workTypeText}</strong>{' '}
                work <TaskTitleLink taskId={taskId} title={taskTitle} /> and
                marked it as{' '}
                <strong>
                    {reviewStatus ? getReviewStatusName(reviewStatus) : ''}
                </strong>
                {grossWorkTime ? ` after ${grossWorkTime}.` : ''} <br />
                {seniorReviewerName
                    ? `The next review is reserved to the quest breakdown solver: ${seniorReviewerName}`
                    : ''}
            </ActivityHeader>
        );
    }

    return (
        <ActivityHeader>
            <strong>{user}</strong> solved <strong>{workTypeText}</strong> work{' '}
            <TaskTitleLink taskId={taskId} title={taskTitle} />
            {grossWorkTime ? ` after ${grossWorkTime}` : ''}
        </ActivityHeader>
    );
};

export default WorkSolvedActivity;
